import { Text, Input, Flex, Image, Box, useDisclosure } from "@chakra-ui/react";
import { useEffect, useRef, useState, useCallback } from "react";
import { LogsButton } from "@/components/Button";
import { useNavigate } from "react-router-dom";
import * as animationData from "@/assets/loading.json";
import Lottie from "lottie-react";
import usePublicClient from "@/hooks/usePublicClient";
import { debounce } from "lodash";
import { didAddresses } from "@/constants/addresses";
import { DEFAULT_CHAIN_ID } from "@/constants";
import LogsDidAbi from "@/constants/abis/LogsDid.json";
import { toHex, zeroAddress } from "viem";
import ErrorIcon from "@/assets/error.png";
import { useUserInfo } from "@/context/UserInfo";

const CreateOrgSecond = ({
  loading,
  handleConfirm,
}: {
  loading: boolean;
  handleConfirm: (orgName: string) => void;
}) => {
  const navigate = useNavigate();
  const [orgName, setOrgName] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const [isOrgNameAvailable, setIsOrgNameAvailable] = useState<boolean>(true);
  const publicClient = usePublicClient();
  const { senderAddress } = useUserInfo();

  const validateInput = (value: string): boolean => {
    // 只允许字母、数字和普通空格
    return /^[a-zA-Z0-9 ]*$/.test(value);
  };

  // 创建检查组织名称是否可用的函数
  const checkOrgNameAvailability = async (name: string) => {
    try {
      // 这里需要根据你的具体合约调用方法来修改
      const ownerAddress = await publicClient.readContract({
        // 添加你的合约相关参数
        address: didAddresses[DEFAULT_CHAIN_ID]!,
        abi: LogsDidAbi,
        functionName: "resolveAddr",
        args: [toHex(name, { size: 32 })],
      });
      setIsOrgNameAvailable(ownerAddress === zeroAddress);
    } catch (error) {
      console.error("Error checking org name:", error);
      setIsOrgNameAvailable(false);
    }
  };

  // 使用useCallback和debounce创建防抖函数
  const debouncedCheck = useCallback(
    debounce((name: string) => {
      if (name) {
        checkOrgNameAvailability(name);
      } else {
        setIsOrgNameAvailable(false);
      }
    }, 500), // 500ms的防抖延迟
    []
  );

  // 修改输入处理函数
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (validateInput(value) && value.length <= 32) {
      setOrgName(value);
      debouncedCheck(value);
    }
  };

  // 在组件卸载时取消未执行的防抖函数
  useEffect(() => {
    return () => {
      debouncedCheck.cancel();
    };
  }, []);

  useEffect(() => {
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.click();
    }, 50);
  }, []);

  return (
    <Flex
      sx={{
        height: "100%",
        paddingTop: "40px",
      }}
      flexDir={"column"}
      align={"center"}
      justify={loading ? "center" : "flex-start"}
    >
      {loading ? (
        <>
          <Lottie
            animationData={animationData}
            loop={true}
            style={{
              width: "40px",
              height: "40px",
            }}
          />
          <Text
            sx={{
              color: "#D8762B",
              textAlign: "center",
              fontSize: "14px",
              fontWeight: 500,
              marginTop: "12px",
            }}
          >
            Organization in progress...
          </Text>
        </>
      ) : (
        <Flex
          flexDir={"column"}
          align={"center"}
          width={"100%"}
          height={"100%"}
        >
          <Text
            sx={{
              width: "100%",
            }}
          >
            Name Your Organization
          </Text>
          <Box
            sx={{
              width: "100%",
              marginTop: "3px",
              height: "20px",
            }}
          >
            {!isOrgNameAvailable && (
              <Flex align={"center"} gap={"4px"} sx={{}}>
                <Image
                  src={ErrorIcon}
                  sx={{
                    width: "12px",
                    height: "100%",
                  }}
                ></Image>
                <Text
                  sx={{
                    color: "#D60000",
                    fontSize: "12px",
                    fontWeight: "bold",
                    fontStyle: "italic",
                  }}
                >
                  Organization Name Error
                </Text>
              </Flex>
            )}
          </Box>

          <Input
            ref={inputRef}
            variant={"unstyled"}
            placeholder="Input name"
            value={orgName}
            onChange={handleInputChange}
            sx={{
              width: "100%",
              height: "20px",
              border: "1px solid transparent",
              borderRadius: "4px",
              fontSize: "14px",
              padding: "0 0px",
              background: "transparent",
              caretColor: "#D8762B",
              position: "relative",
              marginTop: "4px",
              color: isOrgNameAvailable ? "#333" : "#D60000",
              _placeholder: {
                color: "#dedede",
                fontSize: "14px",
              },
            }}
          />
          <Text
            sx={{
              color: "#BBB",
              fontSize: "12px",
              lineHeight: "20px",
              marginTop: "20px",
            }}
          >
            Once your organization name is confirmed, it cannot be changed.
          </Text>

          <LogsButton
            onClick={() => {
              localStorage.setItem(`orgSkip-${senderAddress}`, "true");
              navigate("/");
            }}
            variant="outlined"
            sx={{
              marginTop: "auto",
            }}
          >
            Skip
          </LogsButton>
          <LogsButton
            onClick={() => {
              if (isOrgNameAvailable && orgName) {
                handleConfirm(orgName);
              }
            }}
            sx={{
              marginTop: "20px",
              background: isOrgNameAvailable && orgName ? "#D8762B" : "#BBB",
              border:
                isOrgNameAvailable && orgName
                  ? "1px solid #D8762B"
                  : "1px solid #BBB",
            }}
          >
            Confirm
          </LogsButton>
        </Flex>
      )}
    </Flex>
  );
};

export default CreateOrgSecond;
